import React, { useState, useEffect } from 'react';
import HttpInterceptor from '../../lib/HttpInterceptor';
import { toast } from 'react-toastify';
import { useNavigate, Link } from 'react-router-dom';
import { 
  FaUsers, 
  FaBoxOpen, 
  FaDollarSign, 
  FaChartLine, 
  FaCheckCircle,
  FaExclamationTriangle,
  FaArrowUp,
  FaArrowDown,
  FaCreditCard,
  FaCogs
} from 'react-icons/fa';

const MarketplaceDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ paymentSystemMode: 'manual' });
  const [updatingSettings, setUpdatingSettings] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await HttpInterceptor.get('/api/admin/public/settings');
      setSettings(response.data.data);
    } catch (error) {
      toast.error('Failed to load global settings');
    }
  };

  const updateSettings = async (newMode) => {
    setUpdatingSettings(true);
    try {
      const response = await HttpInterceptor.put('/api/admin/settings/global', { 
        paymentSystemMode: newMode 
      });
      setSettings(response.data.data);
      toast.success(`Payment mode switched to ${newMode}`);
    } catch (error) {
      toast.error('Failed to update payment settings');
    } finally {
      setUpdatingSettings(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await HttpInterceptor.get('/api/admin/marketplace/stats');
      setStats(response.data.data);
    } catch (error) {
      toast.error('Failed to load marketplace stats');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
        <h1 className="text-3xl font-bold mb-6">Marketplace Overview</h1>

        {/* Welcome Banner */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl shadow-lg p-6 mb-8 text-white">
          <h2 className="text-2xl font-bold mb-2">Monitor Your Marketplace</h2>
          <p className="text-purple-100">Track performance, manage approvals, and view commission reports</p>
        </div>

        {/* Stats Grid with Icons */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-purple-600">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-gray-600 text-sm font-medium">Total Resellers</h3>
              <FaUsers className="text-purple-600 text-xl" />
            </div>
            <p className="text-3xl font-bold text-purple-600 mb-1">{stats?.resellers?.total || 0}</p>
            {stats?.resellers?.pendingApplications > 0 && (
              <div className="flex items-center gap-1 text-sm text-yellow-600">
                <FaExclamationTriangle />
                <span>{stats.resellers.pendingApplications} pending</span>
              </div>
            )}
          </div>
          
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-blue-600">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-gray-600 text-sm font-medium">Total Products</h3>
              <FaBoxOpen className="text-blue-600 text-xl" />
            </div>
            <p className="text-3xl font-bold text-blue-600 mb-1">{stats?.products?.total || 0}</p>
            {stats?.products?.pendingApprovals > 0 && (
              <div className="flex items-center gap-1 text-sm text-yellow-600">
                <FaExclamationTriangle />
                <span>{stats.products.pendingApprovals} pending</span>
              </div>
            )}
          </div>
          
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-green-600">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-gray-600 text-sm font-medium">Total Revenue</h3>
              <FaDollarSign className="text-green-600 text-xl" />
            </div>
            <p className="text-3xl font-bold text-green-600 mb-1">
              ₹{(stats?.orders?.totalRevenue || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
            </p>
            <p className="text-sm text-gray-500">{stats?.orders?.total || 0} orders</p>
          </div>
          
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-orange-600">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-gray-600 text-sm font-medium">Platform Commission</h3>
              <FaChartLine className="text-orange-600 text-xl" />
            </div>
            <p className="text-3xl font-bold text-orange-600 mb-1">
              ₹{(stats?.orders?.totalCommission || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
            </p>
            <p className="text-sm text-gray-500">
              Commission from all orders
            </p>
          </div>
        </div>

        {/* Pending Approvals Alert */}
        {(stats?.resellers?.pendingApplications > 0 || stats?.products?.pendingApprovals > 0) && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg mb-8">
            <div className="flex items-center gap-2 mb-2">
              <FaExclamationTriangle className="text-yellow-600" />
              <h3 className="font-semibold text-yellow-800">Pending Approvals</h3>
            </div>
            <div className="flex flex-wrap gap-4 text-sm">
              {stats.resellers?.pendingApplications > 0 && (
                <Link
                  to="/admin/reseller-approvals"
                  className="text-yellow-700 hover:text-yellow-900 underline"
                >
                  {stats.resellers.pendingApplications} reseller application(s) need review
                </Link>
              )}
              {stats.products?.pendingApprovals > 0 && (
                <Link
                  to="/admin/product-approvals"
                  className="text-yellow-700 hover:text-yellow-900 underline"
                >
                  {stats.products.pendingApprovals} product(s) need approval
                </Link>
              )}
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Link
            to="/admin/reseller-approvals"
            className="bg-white rounded-xl shadow-md p-6 text-left hover:shadow-lg transition-shadow border-2 border-transparent hover:border-purple-500"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-purple-100 p-3 rounded-lg">
                <FaUsers className="text-purple-600 text-2xl" />
              </div>
              <div>
                <h3 className="text-xl font-bold">Reseller Approvals</h3>
                {stats?.resellers?.pendingApplications > 0 && (
                  <span className="inline-block mt-1 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                    {stats.resellers.pendingApplications} pending
                  </span>
                )}
              </div>
            </div>
            <p className="text-gray-600 text-sm">
              Review and approve reseller applications
            </p>
          </Link>
          
          <Link
            to="/admin/product-approvals"
            className="bg-white rounded-xl shadow-md p-6 text-left hover:shadow-lg transition-shadow border-2 border-transparent hover:border-blue-500"
          >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="bg-blue-100 p-3 rounded-lg">
                      <FaBoxOpen className="text-blue-600 text-2xl" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">Product Approvals</h3>
                      {stats?.products?.pendingApprovals > 0 && (
                        <span className="inline-block mt-1 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                          {stats.products.pendingApprovals} pending
                        </span>
                      )}
                    </div>
                  </div>
            <p className="text-gray-600 text-sm">
              Review and approve product listings
            </p>
          </Link>
          
          <Link
            to="/admin/marketplace/commission-report"
            className="bg-white rounded-xl shadow-md p-6 text-left hover:shadow-lg transition-shadow border-2 border-transparent hover:border-green-500"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-green-100 p-3 rounded-lg">
                <FaChartLine className="text-green-600 text-2xl" />
              </div>
              <div>
                <h3 className="text-xl font-bold">Commission Report</h3>
              </div>
            </div>
            <p className="text-gray-600 text-sm">
              View detailed commission and sales reports
            </p>
          </Link>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
              <FaDollarSign className="text-green-600" />
              Revenue Summary
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Revenue</span>
                <span className="font-bold text-lg">
                  ₹{(stats?.totalRevenue || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Platform Commission</span>
                <span className="font-bold text-lg text-green-600">
                  ₹{(stats?.totalPlatformCommission || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Reseller Payouts</span>
                <span className="font-bold text-lg text-blue-600">
                  ₹{(stats?.totalResellerPayoutsProcessed || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between items-center pt-3 border-t">
                <span className="text-gray-600">Pending Payouts</span>
                <span className="font-bold text-lg text-yellow-600">
                  ₹{(stats?.pendingResellerPayouts || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
              <FaBoxOpen className="text-blue-600" />
              Marketplace Activity
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Orders</span>
                <span className="font-bold text-lg">{stats?.orders?.total || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Active Resellers</span>
                <span className="font-bold text-lg text-green-600">{stats?.resellers?.total || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Active Products</span>
                <span className="font-bold text-lg text-blue-600">{stats?.products?.total || 0}</span>
              </div>
              <div className="flex justify-between items-center pt-3 border-t">
                <span className="text-gray-600">Pending Actions</span>
                <span className="font-bold text-lg text-red-600">
                  {(stats?.resellers?.pendingApplications || 0) + (stats?.products?.pendingApprovals || 0)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Configuration Section */}
        <div className="mt-8 bg-white rounded-xl shadow-md p-6 border-l-4 border-purple-600">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold flex items-center gap-2">
              <FaCreditCard className="text-purple-600" />
              Payment System Configuration
            </h3>
            <div className="bg-purple-50 px-3 py-1 rounded-full text-xs font-semibold text-purple-600 border border-purple-200">
              Current Mode: <span className="uppercase">{settings.paymentSystemMode}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div 
              onClick={() => !updatingSettings && updateSettings('manual')}
              className={`p-5 rounded-xl border-2 cursor-pointer transition-all ${
                settings.paymentSystemMode === 'manual' 
                  ? 'border-purple-600 bg-purple-50' 
                  : 'border-gray-200 hover:border-purple-300'
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="font-bold text-lg">Manual Receipt Mode</span>
                {settings.paymentSystemMode === 'manual' && <FaCheckCircle className="text-purple-600" />}
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">
                Orders with digital payment methods (UPI/Card) are set to <span className="font-semibold text-orange-600">Pending</span>. Students must upload a payment receipt for verification.
              </p>
            </div>

            <div 
              onClick={() => !updatingSettings && updateSettings('gateway')}
              className={`p-5 rounded-xl border-2 cursor-pointer transition-all ${
                settings.paymentSystemMode === 'gateway' 
                  ? 'border-blue-600 bg-blue-50' 
                  : 'border-gray-200 hover:border-blue-300'
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="font-bold text-lg">Gateway Callback Mode</span>
                {settings.paymentSystemMode === 'gateway' && <FaCheckCircle className="text-blue-600" />}
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">
                Simulates real-time payment gateway (Razorpay/Stripe). Requires <span className="font-semibold text-green-600">simulated callback</span> to confirm payment and mark as Paid automatically.
              </p>
            </div>
          </div>
          
          {updatingSettings && (
            <div className="flex justify-center mt-4">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600"></div>
            </div>
          )}
        </div>
      </div>
  );
};

export default MarketplaceDashboard;

